import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * Registration form component.
 * Handles user input for registration fields and basic client-side validation.
 * Now includes password confirmation and password visibility toggle.
 */
function RegisterForm({ onSubmit, registerError, isSubmitting }) {
  // Local state for form inputs
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [promocode, setPromocode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);

  // Basic form validation: enable submit only if required fields are filled and passwords match
  const isFormValid =
    name.trim() &&
    username.trim() &&
    password.trim() &&
    confirmPassword.trim() &&
    password === confirmPassword;

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    onSubmit({ name, username, password, promocode });
  };

  // Helper for showing password mismatch error
  const showPasswordMismatch =
    confirmTouched &&
    password &&
    confirmPassword &&
    password !== confirmPassword;

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* Display registration error if present */}
      {registerError && (
        <div className="alert alert-danger" role="alert">
          {registerError}
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="nameInput" className="form-label">
          Name <span className="text-danger">*</span>
        </label>
        <input
          id="nameInput"
          type="text"
          className="form-control"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoComplete="name"
        />
      </div>

      <div className="mb-3">
        <label htmlFor="usernameInput" className="form-label">
          Username (Mobile) <span className="text-danger">*</span>
        </label>
        <input
          id="usernameInput"
          type="text"
          className="form-control"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          autoComplete="username"
        />
      </div>

      <div className="mb-3" style={{ position: "relative" }}>
        <label htmlFor="passwordInput" className="form-label">
          Password <span className="text-danger">*</span>
        </label>
        <input
          id="passwordInput"
          type={showPassword ? "text" : "password"}
          className="form-control"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="new-password"
        />
        <button
          type="button"
          tabIndex={-1}
          className="btn btn-outline-secondary"
          style={{
            position: "absolute",
            right: "10px",
            top: "38px",
            padding: "2px 8px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontSize: "1.2rem",
            zIndex: 2,
          }}
          aria-label={showPassword ? "Hide password" : "Show password"}
          onClick={() => setShowPassword((show) => !show)}
        >
          {showPassword ? "🙈" : "👁️"}
        </button>
      </div>

      <div className="mb-3" style={{ position: "relative" }}>
        <label htmlFor="confirmPasswordInput" className="form-label">
          Confirm Password <span className="text-danger">*</span>
        </label>
        <input
          id="confirmPasswordInput"
          type={showConfirmPassword ? "text" : "password"}
          className="form-control"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          autoComplete="new-password"
          onBlur={() => setConfirmTouched(true)}
        />
        <button
          type="button"
          tabIndex={-1}
          className="btn btn-outline-secondary"
          style={{
            position: "absolute",
            right: "10px",
            top: "38px",
            padding: "2px 8px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontSize: "1.2rem",
            zIndex: 2,
          }}
          aria-label={
            showConfirmPassword
              ? "Hide confirm password"
              : "Show confirm password"
          }
          onClick={() => setShowConfirmPassword((show) => !show)}
        >
          {showConfirmPassword ? "🙈" : "👁️"}
        </button>
        {showPasswordMismatch && (
          <div className="form-text text-danger">Passwords do not match.</div>
        )}
      </div>

      <div className="mb-3">
        <label htmlFor="promoInput" className="form-label">
          Promo Code (Optional)
        </label>
        <input
          id="promoInput"
          type="text"
          className="form-control"
          value={promocode}
          onChange={(e) => setPromocode(e.target.value)}
          autoComplete="off"
        />
      </div>

      <button
        type="submit"
        className="btn btn-primary"
        disabled={!isFormValid || isSubmitting}
      >
        {isSubmitting ? "Registering..." : "Register"}
      </button>
    </form>
  );
}

// Add PropTypes validation for props
RegisterForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  registerError: PropTypes.string,
  isSubmitting: PropTypes.bool.isRequired,
};

export default RegisterForm;
